import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import
{
    faUser,
    faRightFromBracket,
    faCartShopping,
    faBoxOpen,
    faEnvelope,
    faIdCard,
    faTrash,
    faHeart,
    faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import Cookie from "cookie-universal";
import { Axios } from "../../../API/Axios";
import { USER, LOGOUT } from "../../../API/Api";
import { useLanguage } from "../../../Context/LanguageContext";
import { useCart } from "../../../Context/CartChangerContext";
import { useWishlist } from "../../../Context/WishlistContext";
import "./Profile.css";

const cookieJar = Cookie();

// يحسب السعر بعد الخصم (discount نسبة مئوية)
function priceAfterDiscount ( price, discount )
{
    const p = Number( price ) || 0;
    const d = Number( discount ) || 0;
    return d > 0 ? p - ( p * d ) / 100 : p;
}

const STATUS_CLASS = {
    pending: "status-pending",
    processing: "status-processing",
    shipped: "status-shipped",
    delivered: "status-delivered",
    cancelled: "status-cancelled",
};

export default function Profile ()
{
    const { t } = useLanguage();
    const navigate = useNavigate();

    const [ user, setUser ] = useState( null );
    const [ loading, setLoading ] = useState( true );
    const [ loggingOut, setLoggingOut ] = useState( false );

    const { cart, loading: cartLoading, removeFromCart } = useCart();
    const { wishlist, loading: wishlistLoading, removeFromWishlist } = useWishlist();

    const [ orders, setOrders ] = useState( [] );
    const [ ordersLoading, setOrdersLoading ] = useState( true );

    const [ activeTab, setActiveTab ] = useState( "info" );

    const token = cookieJar.get( "ecommerce" );

    useEffect( () =>
    {
        if ( !token )
        {
            setLoading( false );
            setOrdersLoading( false );
            return;
        }

        Axios.get( `/${ USER }` )
            .then( ( res ) => setUser( res.data ) )
            .catch( () => setUser( null ) )
            .finally( () => setLoading( false ) );

        Axios.get( `/orders` )
            .then( ( res ) => setOrders( res.data ) )
            .catch( () => setOrders( [] ) )
            .finally( () => setOrdersLoading( false ) );
    }, [ token ] );

    async function handleLogout ()
    {
        setLoggingOut( true );
        try
        {
            await Axios.get( `/${ LOGOUT }` );
        } catch ( error )
        {
            console.log( error );
        } finally
        {
            cookieJar.remove( "ecommerce" );
            navigate( "/", { replace: true } );
        }
    }

    function handleCheckout ()
    {
        navigate( "/checkout" );
    }

    if ( loading )
    {
        return <div className="profile-page" />;
    }

    if ( !token || !user )
    {
        return (
            <div className="profile-page">
                <div className="profile-card">
                    <div className="profile-guest-icon">
                        <FontAwesomeIcon icon={ faUser } />
                    </div>
                    <h1 className="profile-guest-title">{ t( 'profile.notLoggedInTitle' ) }</h1>
                    <p className="profile-guest-subtitle">{ t( 'profile.notLoggedInSubtitle' ) }</p>
                    <Link to="/auth" className="profile-login-cta">
                        <FontAwesomeIcon icon={ faUser } />
                        { t( 'profile.loginCta' ) }
                    </Link>
                </div>
            </div>
        );
    }

    const initial = user.name ? user.name.charAt( 0 ).toUpperCase() : "?";

    const cartTotal = ( cart || [] ).reduce( ( sum, item ) =>
    {
        const unitPrice = priceAfterDiscount( item.product?.price, item.product?.discount );
        return sum + unitPrice * item.count;
    }, 0 );

    return (
        <div className="profile-page">
            <div className="profile-layout">

                <div className="profile-card profile-card--side">
                    <div className="profile-avatar">{ initial }</div>
                    <h1 className="profile-name">{ user.name }</h1>
                    <p className="profile-email-small">{ user.email }</p>

                    <button className="profile-logout-btn" onClick={ handleLogout } disabled={ loggingOut }>
                        <FontAwesomeIcon icon={ faRightFromBracket } />
                        { t( 'profile.logout' ) }
                    </button>

                    <Link to="/orders" className="profile-orders-link">
                        <FontAwesomeIcon icon={ faBoxOpen } />
                        { t( 'profile.viewAllOrders' ) }
                        <FontAwesomeIcon icon={ faArrowLeft } className="profile-orders-link-arrow" />
                    </Link>
                </div>

                <div className="profile-main">

                    <div className="profile-tabs">
                        <button
                            className={ `profile-tab ${ activeTab === "info" ? "is-active" : "" }` }
                            onClick={ () => setActiveTab( "info" ) }
                        >
                            <FontAwesomeIcon icon={ faIdCard } />
                            <span>{ t( 'profile.tabInfo' ) }</span>
                        </button>
                        <button
                            className={ `profile-tab ${ activeTab === "cart" ? "is-active" : "" }` }
                            onClick={ () => setActiveTab( "cart" ) }
                        >
                            <FontAwesomeIcon icon={ faCartShopping } />
                            <span>{ t( 'profile.tabCart' ) }</span>
                            { cart && cart.length > 0 && (
                                <span className="profile-tab-badge">{ cart.length }</span>
                            ) }
                        </button>
                        <button
                            className={ `profile-tab ${ activeTab === "wishlist" ? "is-active" : "" }` }
                            onClick={ () => setActiveTab( "wishlist" ) }
                        >
                            <FontAwesomeIcon icon={ faHeart } />
                            <span>{ t( 'profile.tabWishlist' ) }</span>
                            { wishlist && wishlist.length > 0 && (
                                <span className="profile-tab-badge">{ wishlist.length }</span>
                            ) }
                        </button>
                        <button
                            className={ `profile-tab ${ activeTab === "orders" ? "is-active" : "" }` }
                            onClick={ () => setActiveTab( "orders" ) }
                        >
                            <FontAwesomeIcon icon={ faBoxOpen } />
                            <span>{ t( 'profile.tabOrders' ) }</span>
                            { orders && orders.length > 0 && (
                                <span className="profile-tab-badge">{ orders.length }</span>
                            ) }
                        </button>
                    </div>

                    {/* ------------------ البيانات الشخصية ------------------ */ }
                    { activeTab === "info" && (
                        <div className="profile-panel">
                            <div className="profile-field">
                                <span className="profile-field-label">
                                    <FontAwesomeIcon icon={ faUser } /> { t( 'profile.nameLabel' ) }
                                </span>
                                <span className="profile-field-value">{ user.name }</span>
                            </div>
                            <div className="profile-field">
                                <span className="profile-field-label">
                                    <FontAwesomeIcon icon={ faEnvelope } /> { t( 'profile.emailLabel' ) }
                                </span>
                                <span className="profile-field-value">{ user.email }</span>
                            </div>
                            { user.phone && (
                                <div className="profile-field">
                                    <span className="profile-field-label">{ t( 'profile.phoneLabel' ) }</span>
                                    <span className="profile-field-value">{ user.phone }</span>
                                </div>
                            ) }
                            { user.created_at && (
                                <div className="profile-field">
                                    <span className="profile-field-label">{ t( 'profile.joinedLabel' ) }</span>
                                    <span className="profile-field-value">
                                        { new Date( user.created_at ).toLocaleDateString( 'ar-EG' ) }
                                    </span>
                                </div>
                            ) }
                        </div>
                    ) }

                    {/* ------------------ السلة الحالية ------------------ */ }
                    { activeTab === "cart" && (
                        <div className="profile-panel">
                            { cartLoading ? (
                                <p className="profile-empty-msg">{ t( 'profile.loading' ) }</p>
                            ) : !cart || cart.length === 0 ? (
                                <div className="profile-empty-state">
                                    <FontAwesomeIcon icon={ faCartShopping } />
                                    <p>{ t( 'profile.emptyCart' ) }</p>
                                    <Link to="/shop" className="profile-login-cta">
                                        { t( 'profile.browseShop' ) }
                                    </Link>
                                </div>
                            ) : (
                                <>
                                    <div className="profile-list">
                                        { cart.map( ( item ) =>
                                        {
                                            const product = item.product || {};
                                            const img = product.images?.[ 0 ]?.image;
                                            const unitPrice = priceAfterDiscount( product.price, product.discount );
                                            const hasDiscount = Number( product.discount ) > 0;

                                            return (
                                                <div className="profile-list-row" key={ item.id }>
                                                    <div className="profile-list-img-wrap">
                                                        { img ? (
                                                            <img src={ img } alt={ product.title || "" } className="profile-list-img" />
                                                        ) : (
                                                            <div className="profile-list-img profile-list-img--placeholder" />
                                                        ) }
                                                    </div>
                                                    <div className="profile-list-info">
                                                        <p className="profile-list-title">{ product.title }</p>
                                                        <p className="profile-list-sub">
                                                            { item.count } × { unitPrice.toFixed( 2 ) } { t( 'currency' ) }
                                                            { hasDiscount && (
                                                                <span className="profile-list-old-price">
                                                                    { Number( product.price ).toFixed( 2 ) }
                                                                </span>
                                                            ) }
                                                        </p>
                                                    </div>
                                                    <button
                                                        className="profile-list-remove"
                                                        onClick={ () => removeFromCart( item.id ) }
                                                        aria-label="remove"
                                                    >
                                                        <FontAwesomeIcon icon={ faTrash } />
                                                    </button>
                                                </div>
                                            );
                                        } ) }
                                    </div>
                                    <div className="profile-cart-total">
                                        <span>{ t( 'profile.total' ) }</span>
                                        <strong>{ cartTotal.toFixed( 2 ) } { t( 'currency' ) }</strong>
                                    </div>

                                    <button
                                        className="profile-checkout-btn"
                                        onClick={ handleCheckout }
                                    >
                                        { t( 'profile.continueToCheckout' ) }
                                    </button>
                                </>
                            ) }
                        </div>
                    ) }

                    {/* ------------------ المفضلة ------------------ */ }
                    { activeTab === "wishlist" && (
                        <div className="profile-panel">
                            { wishlistLoading ? (
                                <p className="profile-empty-msg">{ t( 'profile.loading' ) }</p>
                            ) : !wishlist || wishlist.length === 0 ? (
                                <div className="profile-empty-state">
                                    <FontAwesomeIcon icon={ faHeart } />
                                    <p>{ t( 'profile.emptyWishlist' ) }</p>
                                    <Link to="/shop" className="profile-login-cta">
                                        { t( 'profile.browseShop' ) }
                                    </Link>
                                </div>
                            ) : (
                                <div className="profile-list">
                                    { wishlist.map( ( item ) =>
                                    {
                                        const product = item.product || {};
                                        const img = product.images?.[ 0 ]?.image;
                                        const unitPrice = priceAfterDiscount( product.price, product.discount );
                                        const hasDiscount = Number( product.discount ) > 0;

                                        return (
                                            <div className="profile-list-row" key={ item.id }>
                                                <Link to={ `/product/${ product.id }` } className="profile-list-img-wrap">
                                                    { img ? (
                                                        <img src={ img } alt={ product.title || "" } className="profile-list-img" />
                                                    ) : (
                                                        <div className="profile-list-img profile-list-img--placeholder" />
                                                    ) }
                                                </Link>
                                                <div className="profile-list-info">
                                                    <Link to={ `/product/${ product.id }` } className="profile-list-title-link">
                                                        <p className="profile-list-title">{ product.title }</p>
                                                    </Link>
                                                    <p className="profile-list-sub">
                                                        { unitPrice.toFixed( 2 ) } { t( 'currency' ) }
                                                        { hasDiscount && (
                                                            <span className="profile-list-old-price">
                                                                { Number( product.price ).toFixed( 2 ) }
                                                            </span>
                                                        ) }
                                                    </p>
                                                </div>
                                                <button
                                                    className="profile-list-remove"
                                                    onClick={ () => removeFromWishlist( product.id ) }
                                                    aria-label="remove"
                                                >
                                                    <FontAwesomeIcon icon={ faTrash } />
                                                </button>
                                            </div>
                                        );
                                    } ) }
                                </div>
                            ) }
                        </div>
                    ) }

                    {/* ------------------ الطلبات السابقة ------------------ */ }
                    { activeTab === "orders" && (
                        <div className="profile-panel">
                            { ordersLoading ? (
                                <p className="profile-empty-msg">{ t( 'profile.loading' ) }</p>
                            ) : !orders || orders.length === 0 ? (
                                <div className="profile-empty-state">
                                    <FontAwesomeIcon icon={ faBoxOpen } />
                                    <p>{ t( 'profile.noOrders' ) }</p>
                                </div>
                            ) : (
                                <div className="profile-orders">
                                    { orders.map( ( order ) =>
                                    {
                                        const statusText = t( `orders.status.${ order.status }` );
                                        const statusClass = STATUS_CLASS[ order.status ] || STATUS_CLASS.pending;
                                        return (
                                            <Link to={ `/orders/${ order.id }` } className="profile-order-card profile-order-card--link" key={ order.id }>
                                                <div className="profile-order-header">
                                                    <span className="profile-order-id">{ t( "orders.orderNumber", { id: order.id } ) }</span>
                                                    <div className="od-status-badges">
                                                        <span className={ `profile-order-status ${ statusClass }` }>
                                                            { statusText }
                                                        </span>
                                                        <span className={ `od-payment-badge od-payment-badge--${ order.payment_status }` }>
                                                            { t( `orders.paymentStatus.${ order.payment_status }` ) }
                                                        </span>
                                                    </div>
                                                </div>
                                                <p className="profile-order-date">
                                                    { new Date( order.created_at ).toLocaleDateString( 'ar-EG' ) }
                                                </p>
                                                <div className="profile-list">
                                                    { order.items?.map( ( orderItem ) =>
                                                    {
                                                        const product = orderItem.product || {};
                                                        const img = product.images?.[ 0 ]?.image;
                                                        return (
                                                            <div className="profile-list-row" key={ orderItem.id }>
                                                                <div className="profile-list-img-wrap">
                                                                    { img ? (
                                                                        <img src={ img } alt={ product.title || "" } className="profile-list-img" />
                                                                    ) : (
                                                                        <div className="profile-list-img profile-list-img--placeholder" />
                                                                    ) }
                                                                </div>
                                                                <div className="profile-list-info">
                                                                    <p className="profile-list-title">{ product.title }</p>
                                                                    <p className="profile-list-sub">
                                                                        { orderItem.count } × { Number( orderItem.price ).toFixed( 2 ) } { t( 'currency' ) }
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        );
                                                    } ) }
                                                </div>
                                                <div className="profile-cart-total">
                                                    <span>{ t( 'profile.total' ) }</span>
                                                    <strong>{ Number( order.total ).toFixed( 2 ) } { t( 'currency' ) }</strong>
                                                </div>
                                            </Link>
                                        );
                                    } ) }
                                </div>
                            ) }
                        </div>
                    ) }

                </div>
            </div>
        </div>
    );
}
